import { useTranslation } from 'react-i18next'
import { useDebounceCallback } from 'usehooks-ts'

import { getMinAmount } from '@entities/Product/helpers'

import { useAuth } from '@features/auth'
import { useCartStore } from '@features/cart/model/useCartStore'
import { ProductCounter } from '@features/cart/ui/product-counter'
import useAddress from '@features/delivery/hooks/useAddress'
import { useErrorInfoModal } from '@features/modal'
import { useAddressStore } from '@features/shipping-methods'


import { DEBOUNCE_TIMEOUT_UPDATE_AMOUNT } from '@shared/contants/product'
import { Product, useUpdateCartProductAmountMutation } from '@shared/generated'
import { cn } from '@shared/lib/cn'
import Button from '@shared/ui/button'

import AddToCart from './add-to-cart'


interface ControlCartProps {
  classNameAddButton?: string;
  classNameControl?: string;
  isTablet: boolean;
  size?: 'small';
  product: Product;
  isInTabBar?: boolean;
}

export default function ControlCart (props: ControlCartProps) {
  const {
    classNameAddButton,
    isTablet,
    classNameControl,
    size,
    product,
    isInTabBar,
  } = props
  const { t: te } = useTranslation('errors')
  const { t: tp } = useTranslation('product')
  const { isAuthorized } = useAuth()
  const { addOrUpdateProduct, isProductInCart, removeProduct } = useCartStore()
  const { openErrorInfoModal } = useErrorInfoModal()
  const [ updateProductAmount ] = useUpdateCartProductAmountMutation()
  const debouncedUpdate = useDebounceCallback(
    updateProductAmount,
    DEBOUNCE_TIMEOUT_UPDATE_AMOUNT,
  )
  const { shopId } = useAddressStore()

  const HAS_PRODUCT_IN_CART = isProductInCart(product.id)
  const IS_OUT_OF_STOCK = product.stock?.amount === 0

  const handleAddToCart = async () => {
    const minAmount = getMinAmount(product)
    addOrUpdateProduct({
      product_id: product.id,
      amount: minAmount,
    })

    try {
      if (isAuthorized) {
        await debouncedUpdate({
          variables: {
            shopId: shopId,
            itemData: {
              product_id: product.id,
              amount: minAmount,
            },
          },
        })
      }
    }
    catch (error) {
      console.error('Failed to sync add to cart:', error)
      openErrorInfoModal({
        description: te('add_to_cart'),
      })
      removeProduct(product.id)
    }
  }

  const addressFlow = useAddress(handleAddToCart)

  if (IS_OUT_OF_STOCK && !HAS_PRODUCT_IN_CART) {
    return (
      <Button
        className={cn(
          classNameAddButton,
          isInTabBar ? 'is_out_of_stock_tabbar' : null,
        )}
        size={isTablet ? 'medium' : 'large'}
        disabled
      >
        {tp('product.out_of_stock')}
      </Button>
    )
  }

  if (!HAS_PRODUCT_IN_CART) {
    return (
      <AddToCart
        className={cn(
          classNameAddButton,
          isInTabBar ? 'add_to_cart_tabbar' : null,
        )}
        isTablet={isTablet}
        onClick={addressFlow}
      />
    )
  }

  return (
    <ProductCounter
      shopId={shopId}
      className={classNameControl}
      size={size}
      product={product}
      invertInfo={isInTabBar}
    />
  )
}
